'use client'

import { useState, useEffect, Suspense } from 'react'
import { useSignIn, useUser } from '@clerk/nextjs'
import { useRouter, useSearchParams } from 'next/navigation'
import Link from 'next/link'
import { motion } from 'framer-motion'
import { ArrowRight, Mail, Lock, AlertCircle, Loader2, Eye, EyeOff, Home } from 'lucide-react'
import { GeometricBackground } from '@/components/ui/geometric-background'

function LoginForm() {
  const { isLoaded, signIn, setActive } = useSignIn()
  const { isSignedIn } = useUser()
  const searchParams = useSearchParams()
  const redirectUrl = searchParams.get('redirect_url') || '/dashboard'
  const [emailAddress, setEmailAddress] = useState('')
  const [password, setPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)
  const [googleLoading, setGoogleLoading] = useState(false)
  const [needs2FA, setNeeds2FA] = useState(false)
  const [code, setCode] = useState('')
  const router = useRouter()

  // If user is already signed in, redirect them away from login page
  useEffect(() => {
    if (isSignedIn) {
      router.replace(redirectUrl)
    }
  }, [isSignedIn, redirectUrl, router])

  // Handle standard email/password login
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!isLoaded) return

    setLoading(true)
    setError('')

    try {
      if (needs2FA) {
        const result = await signIn.attemptSecondFactor({
          strategy: 'email_code',
          code,
        })
        if (result.status === 'complete') {
          await setActive({ session: result.createdSessionId })
          router.push('/dashboard')
        } else {
          // Unexpected auth state
        }
      } else {
        const result = await signIn.create({
          identifier: emailAddress,
          password,
        })

        if (result.status === 'complete') {
          await setActive({ session: result.createdSessionId })
          router.push(redirectUrl)
        } else if (result.status === 'needs_second_factor') {
          setNeeds2FA(true)
          if (result.supportedFirstFactors?.some(f => f.strategy === 'email_code')) {
            await signIn.prepareSecondFactor({ strategy: 'email_code' })
          } else {
            // Let Clerk handle default behavior if already sent
          }
        } else {
          // Unexpected auth state
        }
      }
    } catch (err: any) {
      setError(err.errors?.[0]?.longMessage || 'Invalid credentials or code.')
    } finally {
      setLoading(false)
    }
  }

  // Handle Google OAuth sign-in
  const handleGoogleSignIn = async () => {
    if (!isLoaded || googleLoading) return
    setGoogleLoading(true)
    setError('')
    try {
      await signIn.authenticateWithRedirect({
        strategy: 'oauth_google',
        redirectUrl: '/sso-callback',
        redirectUrlComplete: redirectUrl,
      })
    } catch (err: any) {
      setGoogleLoading(false)
      setError(err.errors?.[0]?.longMessage || 'Google sign-in failed. Please try again.')
    }
  }

  return (
    <div className="relative min-h-svh w-full flex items-center justify-center p-6 bg-black overflow-hidden">
      <GeometricBackground />

      {/* Back to Home Button */}
      <div className="absolute top-6 left-6 z-20">
        <Link
          href="/"
          className="flex items-center gap-2 px-4 py-2 rounded-xl bg-white/[0.03] border border-white/[0.08] text-white/50 hover:text-white hover:bg-white/[0.06] transition-all text-sm font-medium backdrop-blur-sm group"
        >
          <Home className="w-4 h-4 group-hover:scale-110 transition-transform" />
          Back to Home
        </Link>
      </div>

      <div className="relative z-10 w-full max-w-md">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="rounded-3xl border border-white/[0.08] bg-white/[0.02] backdrop-blur-xl p-8 shadow-2xl"
        >
          <div className="text-center mb-8">
            <h1 className="text-3xl font-bold text-white tracking-tight mb-2">Welcome Back</h1>
            <p className="text-white/40 text-sm">Sign in to manage your marketing and branding ecosystem</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5">
            {needs2FA ? (
              <div className="space-y-4">
                <div className="mb-4">
                  <p className="text-white/60 text-sm">We've sent a one-time code to your email. Please enter it below to verify.</p>
                </div>
                <div>
                  <label className="block text-xs font-medium text-white/50 mb-1.5 ml-1 uppercase tracking-wider">
                    Verification Code
                  </label>
                  <input
                    type="text"
                    required
                    value={code}
                    onChange={(e) => setCode(e.target.value)}
                    className="w-full bg-white/[0.03] border border-white/[0.08] rounded-xl py-3 px-4 text-white text-center tracking-widest font-mono focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary/40 transition-all shadow-inner"
                  />
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                <div>
                  <label className="block text-xs font-medium text-white/50 mb-1.5 ml-1 uppercase tracking-wider">
                    Email Address
                  </label>
                  <div className="relative group">
                    <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none text-white/20 group-focus-within:text-primary transition-colors">
                      <Mail className="w-4 h-4" />
                    </div>
                    <input
                      type="email"
                      required
                      value={emailAddress}
                      onChange={(e) => setEmailAddress(e.target.value)}
                      className="w-full bg-white/[0.03] border border-white/[0.08] rounded-xl py-3 pl-11 pr-4 text-white focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary/40 transition-all shadow-inner"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-medium text-white/50 mb-1.5 ml-1 uppercase tracking-wider">
                    Password
                  </label>
                  <div className="relative group">
                    <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none text-white/20 group-focus-within:text-primary transition-colors">
                      <Lock className="w-4 h-4" />
                    </div>
                    <input
                      type={showPassword ? 'text' : 'password'}
                      required
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="w-full bg-white/[0.03] border border-white/[0.08] rounded-xl py-3 pl-11 pr-12 text-white focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary/40 transition-all font-mono shadow-inner"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute inset-y-0 right-0 pr-4 flex items-center text-white/20 hover:text-white/40 transition-colors"
                    >
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                  <div className="flex justify-end mt-2">
                    <Link
                      href="/auth/forgot-password"
                      className="text-xs text-primary/60 hover:text-primary transition-colors font-medium"
                    >
                      Forgot Password?
                    </Link>
                  </div>
                </div>
              </div>
            )}

            {error && (
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                className="flex items-center gap-2 p-3 rounded-xl bg-red-500/10 border border-red-500/20 text-red-400 text-xs"
              >
                <AlertCircle className="w-4 h-4 shrink-0" />
                <span>{error}</span>
              </motion.div>
            )}

            <button
              type="submit"
              disabled={loading || googleLoading}
              className="w-full h-12 bg-primary hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-xl font-semibold flex items-center justify-center gap-2 transition-all active:scale-[0.98] mt-2 shadow-lg shadow-primary/20"
            >
              {loading ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <>
                  Sign In
                  <ArrowRight className="w-4 h-4" />
                </>
              )}
            </button>

            {/* Divider */}
            {!needs2FA && (
              <>
                <div className="relative flex items-center gap-4 my-1">
                  <div className="flex-1 h-px bg-white/[0.08]" />
                  <span className="text-[11px] text-white/25 uppercase tracking-widest font-medium">or</span>
                  <div className="flex-1 h-px bg-white/[0.08]" />
                </div>

                {/* Google Sign In Button */}
                <button
                  type="button"
                  onClick={handleGoogleSignIn}
                  disabled={googleLoading}
                  className={`w-full h-12 border text-white rounded-xl font-medium flex items-center justify-center gap-3 transition-all active:scale-[0.98] group ${
                    googleLoading
                      ? 'bg-white/[0.06] border-primary/30 cursor-not-allowed'
                      : 'bg-white/[0.04] hover:bg-white/[0.08] border-white/[0.08] hover:border-white/[0.15]'
                  }`}
                >
                  {googleLoading ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin text-primary" />
                      <span className="text-sm text-white/70">Redirecting to Google...</span>
                    </>
                  ) : (
                    <>
                      <svg className="w-5 h-5" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                        <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4" />
                        <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
                        <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05" />
                        <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
                      </svg>
                      <span className="text-sm text-white/70 group-hover:text-white transition-colors">Continue with Google</span>
                    </>
                  )}
                </button>
              </>
            )}

            <div className="text-center mt-6">
              <p className="text-white/30 text-xs">
                Don&apos;t have an account?{' '}
                <Link href="/auth/sign-up" className="text-primary hover:text-primary/80 font-medium transition-colors">
                  Sign Up
                </Link>
              </p>
            </div>
          </form>
        </motion.div>
      </div>
    </div>
  )
}

export default function LoginPage() {
  return (
    <Suspense fallback={
      <div className="min-h-svh w-full flex items-center justify-center bg-black">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    }>
      <LoginForm />
    </Suspense>
  )
}
